import React, { useState, useRef, useEffect, useCallback } from "react";
import { API_BASE, apiRequest, setUnauthorizedHandler } from "./chatbotApi";
import OmnichannelInbox from "./OmnichannelInbox";

const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=Inter:wght@400;500;600&display=swap');
  * { box-sizing: border-box; }
  @keyframes bounceDot {
    0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
    30% { transform: translateY(-4px); opacity: 1; }
  }
  .cb-scroll::-webkit-scrollbar, .cb-sidebar::-webkit-scrollbar { width: 6px; }
  .cb-scroll::-webkit-scrollbar-thumb, .cb-sidebar::-webkit-scrollbar-thumb { background: #D8D3C4; border-radius: 8px; }
  .cb-focusable:focus-visible { outline: 2px solid #4A3B5C; outline-offset: 2px; }
`;

// Sesi disimpan di localStorage supaya bertahan setelah refresh/tutup tab.
// Catatan keamanan: localStorage bisa dibaca skrip apa pun di halaman ini (risiko XSS).
// Untuk keamanan lebih tinggi, pindahkan token ke cookie httpOnly (perlu perubahan backend).
const SESSION_KEY = "chatbot_ai_session";

const VIEW_KEY = "chatbot_ai_view";

function loadStoredView() {
  try {
    return window.localStorage.getItem(VIEW_KEY) === "inbox" ? "inbox" : "chat";
  } catch (err) {
    return "chat";
  }
}

function loadStoredSession() {
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.token && parsed.user && parsed.user.username ? parsed : null;
  } catch (err) {
    return null; // storage tidak tersedia / isi rusak -> anggap belum login
  }
}

function persistSession(session) {
  try {
    if (session) window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else window.localStorage.removeItem(SESSION_KEY);
  } catch (err) {
    /* storage penuh/diblokir: sesi tetap jalan, hanya tidak bertahan setelah refresh */
  }
}

function LoginScreen({ onAuthenticated, notice }) {
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError("Isi username dan password terlebih dahulu.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const data = await apiRequest(mode === "login" ? "/auth/login" : "/auth/register", {
        method: "POST",
        body: { username: username.trim(), password },
      });
      onAuthenticated({ token: data.token, user: data.user });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={styles.page}>
      <style>{GLOBAL_STYLES}</style>
      <form style={styles.loginCard} onSubmit={handleSubmit}>
        <div style={styles.loginAvatar}>A</div>
        <div style={styles.loginTitle}>
          {mode === "login" ? "Masuk ke Asisten AI" : "Buat akun baru"}
        </div>
        <div style={styles.loginSubtitle}>
          {mode === "login"
            ? "Gunakan akun Anda untuk melanjutkan percakapan."
            : "Daftar untuk mulai menyimpan riwayat obrolan Anda."}
        </div>

        {notice && (
          <div style={styles.loginNotice} role="status">
            {notice}
          </div>
        )}

        <label style={styles.label}>Username</label>
        <input
          className="cb-focusable"
          style={styles.textInput}
          type="text"
          placeholder="nama.pengguna"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />

        <label style={styles.label}>Password</label>
        <input
          className="cb-focusable"
          style={styles.textInput}
          type="password"
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        {error && <div style={styles.loginError}>{error}</div>}

        <button className="cb-focusable" style={styles.loginBtn} type="submit" disabled={submitting}>
          {submitting ? "Memproses..." : mode === "login" ? "Masuk" : "Daftar"}
        </button>

        <button
          type="button"
          className="cb-focusable"
          style={styles.switchModeBtn}
          onClick={() => {
            setMode(mode === "login" ? "register" : "login");
            setError("");
          }}
        >
          {mode === "login" ? "Belum punya akun? Daftar" : "Sudah punya akun? Masuk"}
        </button>

        <div style={styles.loginNote}>
          Terhubung ke backend Express + PostgreSQL di {API_BASE}.
        </div>
      </form>
    </div>
  );
}

function ConfirmDialog({ config, onCancel, onConfirm }) {
  const cancelRef = useRef(null);

  useEffect(() => {
    // Fokus awal ke "Batal" supaya Enter tidak langsung menghapus
    if (cancelRef.current) cancelRef.current.focus();
    function onKeyDown(e) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  return (
    <div style={styles.overlay} onClick={onCancel}>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="cb-confirm-title"
        aria-describedby="cb-confirm-message"
        style={styles.dialog}
        onClick={(e) => e.stopPropagation()}
      >
        <div id="cb-confirm-title" style={styles.dialogTitle}>
          {config.title}
        </div>
        <div id="cb-confirm-message" style={styles.dialogMessage}>
          {config.message}
        </div>
        <div style={styles.dialogActions}>
          <button ref={cancelRef} className="cb-focusable" style={styles.dialogCancelBtn} onClick={onCancel}>
            Batal
          </button>
          <button className="cb-focusable" style={styles.dialogDangerBtn} onClick={onConfirm}>
            {config.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function SessionScreen({ message, onRetry, onLogout }) {
  return (
    <div style={styles.page}>
      <style>{GLOBAL_STYLES}</style>
      <div style={{ ...styles.loginCard, alignItems: "center", textAlign: "center" }} role="status">
        <div style={styles.loginAvatar}>A</div>
        <div style={styles.loginSubtitle}>{message}</div>
        {onRetry && (
          <div style={{ display: "flex", gap: 10 }}>
            <button className="cb-focusable" style={styles.loginBtn} onClick={onRetry}>
              Coba lagi
            </button>
            <button className="cb-focusable" style={styles.dialogCancelBtn} onClick={onLogout}>
              Keluar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ChatbotAI() {
  const [session, setSession] = useState(loadStoredSession); // { token, user }
  // "checking": token tersimpan sedang diverifikasi | "offline": server tak terjangkau | "ready"
  const [sessionStatus, setSessionStatus] = useState(() =>
    loadStoredSession() ? "checking" : "ready"
  );
  const [loginNotice, setLoginNotice] = useState("");
  const [view, setView] = useState(() => {
    const stored = loadStoredSession();
    return stored?.user?.role === "agent" || stored?.user?.role === "admin"
      ? "inbox"
      : loadStoredView();
  }); // "chat" | "inbox" (inbox hanya untuk agen)
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [confirm, setConfirm] = useState(null); // { title, message, confirmLabel, onConfirm }
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const token = session?.token;
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const resetChatState = useCallback(() => {
    setConversations([]);
    setActiveId(null);
    setMessages([]);
    setInput("");
    setSelectedFile(null);
    setConfirm(null);
    setError(null);
  }, []);

  const endSession = useCallback(
    (notice) => {
      resetChatState();
      setSession(null);
      setSessionStatus("ready");
      setLoginNotice(notice || "");
    },
    [resetChatState]
  );

  // Simpan/hapus sesi di localStorage setiap kali berubah
  useEffect(() => {
    persistSession(session);
  }, [session]);

  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_KEY, view);
    } catch (err) {
      /* abaikan: tampilan hanya tidak diingat setelah refresh */
    }
  }, [view]);

  useEffect(() => {
    if (session?.user?.role === "agent" || session?.user?.role === "admin") {
      setView("inbox");
    }
  }, [session]);

  // Server menolak token (kedaluwarsa/tidak valid) di request mana pun -> kembali ke login
  useEffect(() => {
    setUnauthorizedHandler(() => endSession("Sesi Anda sudah berakhir. Silakan masuk lagi."));
    return () => setUnauthorizedHandler(null);
  }, [endSession]);

  // Verifikasi token tersimpan saat halaman dimuat ulang
  useEffect(() => {
    if (sessionStatus !== "checking" || !session) return undefined;
    let cancelled = false;
    apiRequest("/auth/me", { token: session.token })
      .then((data) => {
        if (cancelled) return;
        setSession((prev) => (prev ? { ...prev, user: data.user } : prev));
        setSessionStatus("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 401) endSession("Sesi Anda sudah berakhir. Silakan masuk lagi.");
        else setSessionStatus("offline"); // server mati/jaringan putus: jangan paksa logout
      });
    return () => {
      cancelled = true;
    };
  }, [sessionStatus, session, endSession]);

  // Sinkron antar-tab: keluar/masuk di satu tab berlaku di tab lain
  useEffect(() => {
    function onStorage(e) {
      if (e.key !== null && e.key !== SESSION_KEY) return;
      const next = loadStoredSession();
      const current = sessionRef.current;
      if (!next) {
        if (current) endSession("");
      } else if (!current || current.token !== next.token) {
        resetChatState();
        setSessionStatus("ready");
        setSession(next);
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [endSession, resetChatState]);

  const loadConversations = useCallback(
    async (selectFirst) => {
      if (!token) return;
      try {
        const data = await apiRequest("/conversations", { token });
        setConversations(data.conversations);
        if (selectFirst && data.conversations.length) {
          setActiveId(data.conversations[0].id);
        }
      } catch (err) {
        setError(err.message);
      }
    },
    [token]
  );

  useEffect(() => {
    if (token && sessionStatus === "ready") loadConversations(true);
  }, [token, sessionStatus, loadConversations]);

  useEffect(() => {
    if (!token || !activeId) return;
    setLoadingHistory(true);
    apiRequest(`/conversations/${activeId}/messages`, { token })
      .then((data) => setMessages(data.messages))
      .catch((err) => setError(err.message))
      .finally(() => setLoadingHistory(false));
  }, [token, activeId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, 140) + "px";
    }
  }, [input]);

  async function sendMessage() {
    const text = input.trim();
    if ((!text && !selectedFile) || loading || !activeId) return;

    setError(null);
    setLoading(true);

    try {
      let attachment = null;
      if (selectedFile) {
        setUploadingFile(true);
        const form = new FormData();
        form.append("file", selectedFile);
        attachment = await apiRequest(`/conversations/${activeId}/attachments`, {
          method: "POST",
          token,
          body: form,
          isFormData: true,
        });
        setUploadingFile(false);
      }

      setMessages((prev) => [
        ...prev,
        {
          role: "user",
          content: text || "(Berkas terlampir)",
          attachments: attachment ? [attachment] : [],
        },
      ]);
      setInput("");
      setSelectedFile(null);

      const data = await apiRequest(`/conversations/${activeId}/messages`, {
        method: "POST",
        token,
        body: { content: text, attachment },
      });
      setMessages((prev) => [...prev, data.reply]);
      setConversations((prev) =>
        prev
          .map((c) => (c.id === activeId ? { ...c, title: data.title } : c))
          .sort((a, b) => (a.id === activeId ? -1 : b.id === activeId ? 1 : 0))
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setUploadingFile(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  async function newConversation() {
    try {
      const data = await apiRequest("/conversations", { method: "POST", token });
      setConversations((prev) => [data.conversation, ...prev]);
      setActiveId(data.conversation.id);
      setMessages(data.messages);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }

  async function deleteConversation(id) {
    try {
      await apiRequest(`/conversations/${id}`, { method: "DELETE", token });
      const rest = conversations.filter((c) => c.id !== id);
      setConversations(rest);
      if (id === activeId) {
        if (rest.length) {
          setActiveId(rest[0].id);
        } else {
          setActiveId(null);
          setMessages([]);
        }
      }
    } catch (err) {
      setError(err.message);
    }
  }

  async function clearConversation() {
    try {
      const data = await apiRequest(`/conversations/${activeId}/messages`, { method: "DELETE", token });
      setMessages(data.messages);
      setConversations((prev) =>
        prev.map((c) => (c.id === activeId ? { ...c, title: data.conversation.title } : c))
      );
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }

  async function deleteAllConversations() {
    try {
      await apiRequest("/conversations", { method: "DELETE", token });
      setConversations([]);
      setActiveId(null);
      setMessages([]);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }

  async function runConfirmed() {
    const action = confirm && confirm.onConfirm;
    setConfirm(null);
    if (action) await action();
  }

  function handleLogout() {
    endSession("");
  }

  function handleAuthenticated(newSession) {
    setError(null);
    setLoginNotice("");
    setSessionStatus("ready");
    if (newSession.user.role === "agent" || newSession.user.role === "admin") {
      setView("inbox");
    }
    setSession(newSession);
  }

  if (session && sessionStatus === "checking") {
    return <SessionScreen message="Memeriksa sesi..." />;
  }

  if (session && sessionStatus === "offline") {
    return (
      <SessionScreen
        message="Tidak dapat terhubung ke server. Sesi Anda masih tersimpan."
        onRetry={() => setSessionStatus("checking")}
        onLogout={handleLogout}
      />
    );
  }

  if (!session) {
    return <LoginScreen onAuthenticated={handleAuthenticated} notice={loginNotice} />;
  }

  // Peran dari server (dibaca ulang saat /auth/me). Server tetap memeriksa di setiap request.
  const isAgent = session.user.role === "agent" || session.user.role === "admin";

  if (isAgent && view === "inbox") {
    return (
      <div style={styles.inboxPage}>
        <style>{GLOBAL_STYLES}</style>
        <OmnichannelInbox
          session={session}
          onSwitchToChat={() => setView("chat")}
          onLogout={handleLogout}
        />
      </div>
    );
  }

  const activeConversation = conversations.find((c) => c.id === activeId);

  return (
    <div style={styles.page}>
      <style>{GLOBAL_STYLES}</style>

      {confirm && (
        <ConfirmDialog config={confirm} onCancel={() => setConfirm(null)} onConfirm={runConfirmed} />
      )}

      <div style={styles.shell}>
        <aside className="cb-sidebar" style={styles.sidebar}>
          <div style={styles.sidebarHeader}>
            <div style={styles.sidebarUser}>
              <div style={styles.sidebarAvatar}>
                {session.user.username.charAt(0).toUpperCase()}
              </div>
              <div style={styles.sidebarUsername}>{session.user.username}</div>
            </div>
            <button className="cb-focusable" style={styles.logoutBtn} onClick={handleLogout}>
              Keluar
            </button>
          </div>

          {isAgent && (
            <div role="group" aria-label="Pilih tampilan" style={styles.viewSwitch}>
              <button
                className="cb-focusable"
                aria-pressed="true"
                style={{ ...styles.viewSwitchBtn, ...styles.viewSwitchBtnActive }}
              >
                Chat AI
              </button>
              <button
                className="cb-focusable"
                aria-pressed="false"
                style={styles.viewSwitchBtn}
                onClick={() => setView("inbox")}
              >
                Inbox WhatsApp
              </button>
            </div>
          )}

          <button className="cb-focusable" style={styles.newChatBtn} onClick={newConversation}>
            + Percakapan baru
          </button>

          <div style={styles.historyLabel}>Riwayat obrolan</div>
          <div style={styles.historyList}>
            {conversations.map((c) => (
              <div
                key={c.id}
                style={{
                  ...styles.historyItem,
                  ...(c.id === activeId ? styles.historyItemActive : {}),
                }}
                onClick={() => setActiveId(c.id)}
              >
                <span style={styles.historyItemText}>{c.title}</span>
                <button
                  className="cb-focusable"
                  style={styles.historyDeleteBtn}
                  aria-label={`Hapus percakapan ${c.title}`}
                  disabled={loading}
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirm({
                      title: "Hapus percakapan ini?",
                      message: `"${c.title}" beserta seluruh pesannya akan dihapus permanen dan tidak bisa dikembalikan.`,
                      confirmLabel: "Hapus",
                      onConfirm: () => deleteConversation(c.id),
                    });
                  }}
                >
                  ×
                </button>
              </div>
            ))}
            {conversations.length === 0 && (
              <div style={styles.historyEmpty}>Belum ada percakapan tersimpan.</div>
            )}
          </div>

          <button
            className="cb-focusable"
            style={{
              ...styles.clearAllBtn,
              opacity: conversations.length === 0 || loading ? 0.45 : 1,
              cursor: conversations.length === 0 || loading ? "not-allowed" : "pointer",
            }}
            disabled={conversations.length === 0 || loading}
            onClick={() =>
              setConfirm({
                title: "Hapus semua riwayat?",
                message: `Seluruh ${conversations.length} percakapan beserta pesannya akan dihapus permanen dan tidak bisa dikembalikan.`,
                confirmLabel: "Hapus semua",
                onConfirm: deleteAllConversations,
              })
            }
          >
            Hapus semua riwayat
          </button>
        </aside>

        <div style={styles.panel}>
          <header style={styles.header}>
            <div style={styles.avatar}>A</div>
            <div style={{ flex: 1 }}>
              <div style={styles.title}>
                {activeConversation ? activeConversation.title : "Asisten AI"}
              </div>
              <div style={styles.status}>
                <span style={styles.statusDot} />
                Online
              </div>
            </div>
            <button
              className="cb-focusable"
              style={{
                ...styles.clearChatBtn,
                opacity: !activeId || loading ? 0.45 : 1,
                cursor: !activeId || loading ? "not-allowed" : "pointer",
              }}
              disabled={!activeId || loading}
              onClick={() =>
                setConfirm({
                  title: "Kosongkan chat ini?",
                  message: "Semua pesan dalam percakapan ini akan dihapus permanen. Percakapannya tetap ada dan bisa dipakai lagi dari awal.",
                  confirmLabel: "Kosongkan",
                  onConfirm: clearConversation,
                })
              }
            >
              Kosongkan chat
            </button>
          </header>

          <div className="cb-scroll" style={styles.messages} ref={scrollRef}>
            {loadingHistory && <div style={styles.historyEmpty}>Memuat riwayat...</div>}

            {!loadingHistory &&
              messages.map((m, i) => (
                <div
                  key={i}
                  style={{
                    ...styles.row,
                    justifyContent: m.role === "user" ? "flex-end" : "flex-start",
                  }}
                >
                  <div
                    style={{
                      ...styles.bubble,
                      ...(m.role === "user" ? styles.bubbleUser : styles.bubbleBot),
                    }}
                  >
                    {m.attachments && m.attachments.length > 0 && (
                      <div style={styles.attachmentList}>
                        {m.attachments.map((att, ai) => (
                          <div key={ai} style={styles.attachmentChip}>
                            📎 {att.filename}
                          </div>
                        ))}
                      </div>
                    )}
                    {m.content}
                  </div>
                </div>
              ))}

            {loading && (
              <div style={{ ...styles.row, justifyContent: "flex-start" }}>
                <div style={{ ...styles.bubble, ...styles.bubbleBot, display: "flex", gap: 4 }}>
                  <span style={{ ...styles.dot, animationDelay: "0s" }} />
                  <span style={{ ...styles.dot, animationDelay: "0.15s" }} />
                  <span style={{ ...styles.dot, animationDelay: "0.3s" }} />
                </div>
              </div>
            )}
          </div>

          {error && <div style={styles.errorBox}>{error}</div>}

          {selectedFile && (
            <div style={styles.selectedFileBar}>
              <span style={styles.attachmentChip}>📎 {selectedFile.name}</span>
              <button
                type="button"
                className="cb-focusable"
                style={styles.removeFileBtn}
                onClick={() => setSelectedFile(null)}
                aria-label="Hapus lampiran"
              >
                ×
              </button>
            </div>
          )}

          <div style={styles.inputBar}>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: "none" }}
              onChange={(e) => {
                setSelectedFile(e.target.files[0] || null);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              className="cb-focusable"
              style={styles.attachBtn}
              onClick={() => fileInputRef.current?.click()}
              disabled={!activeId || loading}
              aria-label="Lampirkan berkas"
              title="Lampirkan berkas"
            >
              📎
            </button>
            <textarea
              ref={textareaRef}
              className="cb-focusable"
              style={styles.textarea}
              rows={1}
              placeholder={
                uploadingFile
                  ? "Mengunggah berkas..."
                  : activeId
                  ? "Tulis pesan..."
                  : "Buat percakapan baru untuk mulai"
              }
              value={input}
              disabled={!activeId}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              className="cb-focusable"
              style={{
                ...styles.sendBtn,
                opacity: loading || (!input.trim() && !selectedFile) || !activeId ? 0.5 : 1,
                cursor:
                  loading || (!input.trim() && !selectedFile) || !activeId
                    ? "not-allowed"
                    : "pointer",
              }}
              onClick={sendMessage}
              disabled={loading || (!input.trim() && !selectedFile) || !activeId}
              aria-label="Kirim pesan"
            >
              Kirim
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  viewSwitch: {
    display: "flex",
    gap: 4,
    padding: 3,
    background: "#E7E0CD",
    borderRadius: 12,
    marginBottom: 12,
  },
  viewSwitchBtn: {
    flex: 1,
    border: "none",
    background: "transparent",
    color: "#4A4636",
    fontSize: 12.5,
    padding: "7px 6px",
    borderRadius: 9,
    cursor: "pointer",
    fontFamily: "'Inter', sans-serif",
  },
  viewSwitchBtnActive: { background: "#FFFFFF", color: "#20201C", fontWeight: 600 },
  loginNotice: {
    background: "#F1ECDF",
    color: "#4A4636",
    fontSize: 12.5,
    padding: "8px 10px",
    borderRadius: 10,
    marginBottom: 14,
  },
  /* Dialog konfirmasi & tombol hapus */
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(32,32,28,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    zIndex: 50,
  },
  dialog: {
    width: "100%",
    maxWidth: 380,
    background: "#FFFFFF",
    borderRadius: 18,
    padding: "24px 24px 20px",
    boxShadow: "0 24px 60px -20px rgba(30,26,20,0.4)",
    fontFamily: "'Inter', sans-serif",
  },
  dialogTitle: {
    fontFamily: "'Fraunces', serif",
    fontWeight: 600,
    fontSize: 18,
    color: "#20201C",
    marginBottom: 8,
  },
  dialogMessage: { fontSize: 14, lineHeight: 1.55, color: "#5A5648", marginBottom: 20, wordBreak: "break-word" },
  dialogActions: { display: "flex", justifyContent: "flex-end", gap: 10 },
  dialogCancelBtn: {
    border: "1px solid #E7E3D8",
    background: "#FFFFFF",
    color: "#4A4636",
    fontSize: 14,
    padding: "9px 16px",
    borderRadius: 12,
    cursor: "pointer",
    fontFamily: "'Inter', sans-serif",
  },
  dialogDangerBtn: {
    border: "none",
    background: "#B4472B",
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: 500,
    padding: "9px 16px",
    borderRadius: 12,
    cursor: "pointer",
    fontFamily: "'Inter', sans-serif",
  },
  clearChatBtn: {
    border: "1px solid #E7E3D8",
    background: "transparent",
    color: "#6B6659",
    fontSize: 12.5,
    padding: "7px 12px",
    borderRadius: 999,
    fontFamily: "'Inter', sans-serif",
    flexShrink: 0,
  },
  clearAllBtn: {
    marginTop: 12,
    border: "none",
    background: "transparent",
    color: "#9A3B24",
    fontSize: 12.5,
    padding: "8px 4px",
    textAlign: "left",
    fontFamily: "'Inter', sans-serif",
  },
  page: {
    minHeight: "100vh",
    width: "100%",
    background: "#FAF9F6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    fontFamily: "'Inter', sans-serif",
  },
  inboxPage: {
    minHeight: "100vh",
    height: "100dvh",
    width: "100%",
    background: "#F4F7F5",
    display: "flex",
    alignItems: "stretch",
    justifyContent: "stretch",
    padding: 0,
    fontFamily: "'Inter', sans-serif",
  },
  shell: {
    width: "100%",
    maxWidth: 880,
    height: "min(720px, 90vh)",
    display: "flex",
    borderRadius: 20,
    overflow: "hidden",
    border: "1px solid #E7E3D8",
    boxShadow: "0 20px 50px -20px rgba(30,26,20,0.18)",
  },

  /* Sidebar */
  sidebar: {
    width: 240,
    flexShrink: 0,
    background: "#F1ECDF",
    borderRight: "1px solid #E7E3D8",
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    padding: "16px 14px",
  },
  sidebarHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 14,
  },
  sidebarUser: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
  sidebarAvatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "#31304D",
    color: "#F1ECDF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12.5,
    fontWeight: 600,
    fontFamily: "'Fraunces', serif",
    flexShrink: 0,
  },
  sidebarUsername: {
    fontSize: 13,
    color: "#2A2820",
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  logoutBtn: {
    border: "none",
    background: "transparent",
    color: "#8A6B4F",
    fontSize: 12,
    cursor: "pointer",
    fontFamily: "'Inter', sans-serif",
    padding: "4px 6px",
  },
  newChatBtn: {
    border: "1px solid #D8D0BC",
    background: "#FFFFFF",
    color: "#31304D",
    fontSize: 13,
    fontWeight: 500,
    padding: "9px 12px",
    borderRadius: 12,
    cursor: "pointer",
    fontFamily: "'Inter', sans-serif",
    marginBottom: 16,
  },
  historyLabel: {
    fontSize: 11.5,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#9C9584",
    marginBottom: 8,
    paddingLeft: 4,
  },
  historyList: { display: "flex", flexDirection: "column", gap: 4, flex: 1, overflowY: "auto" },
  historyItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6,
    padding: "8px 10px",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: 13,
    color: "#4A4636",
  },
  historyItemActive: { background: "#FFFFFF", color: "#20201C", fontWeight: 500 },
  historyItemText: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  historyDeleteBtn: {
    border: "none",
    background: "transparent",
    color: "#B0A891",
    fontSize: 15,
    cursor: "pointer",
    lineHeight: 1,
    padding: "0 2px",
    flexShrink: 0,
  },
  historyEmpty: { fontSize: 12.5, color: "#B0A891", padding: "8px 6px" },

  /* Chat panel */
  panel: { flex: 1, minWidth: 0, background: "#FFFFFF", display: "flex", flexDirection: "column" },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "16px 20px",
    borderBottom: "1px solid #EFEBE0",
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: "#31304D",
    color: "#F1ECDF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Fraunces', serif",
    fontWeight: 600,
    fontSize: 17,
    flexShrink: 0,
  },
  title: {
    fontFamily: "'Fraunces', serif",
    fontWeight: 600,
    fontSize: 16,
    color: "#20201C",
    lineHeight: 1.2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  status: { display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#8A8578", marginTop: 2 },
  statusDot: { width: 7, height: 7, borderRadius: "50%", background: "#4C8B6A", display: "inline-block" },
  messages: {
    flex: 1,
    overflowY: "auto",
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    background: "#FEFDFB",
  },
  row: { display: "flex", width: "100%" },
  bubble: {
    maxWidth: "78%",
    padding: "10px 14px",
    borderRadius: 16,
    fontSize: 14.5,
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  bubbleUser: { background: "#31304D", color: "#F4F2EC", borderBottomRightRadius: 4 },
  bubbleBot: { background: "#F1ECDF", color: "#2A2820", borderBottomLeftRadius: 4 },
  attachmentList: { display: "flex", flexDirection: "column", gap: 4, marginBottom: 6 },
  attachmentChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12.5,
    background: "rgba(0,0,0,0.06)",
    borderRadius: 999,
    padding: "3px 10px",
    width: "fit-content",
  },
  selectedFileBar: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    margin: "0 16px 8px",
    padding: "6px 10px",
    background: "#F1ECDF",
    borderRadius: 10,
    width: "fit-content",
  },
  removeFileBtn: {
    border: "none",
    background: "transparent",
    color: "#8A8578",
    fontSize: 15,
    cursor: "pointer",
    lineHeight: 1,
  },
  attachBtn: {
    border: "1px solid #E7E3D8",
    background: "#FFFFFF",
    fontSize: 16,
    borderRadius: 12,
    width: 40,
    height: 40,
    flexShrink: 0,
    cursor: "pointer",
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#9C9584",
    display: "inline-block",
    animation: "bounceDot 1.2s infinite ease-in-out",
  },
  errorBox: {
    margin: "0 20px",
    padding: "8px 12px",
    background: "#FBEAE5",
    color: "#9A3B24",
    fontSize: 13,
    borderRadius: 10,
  },
  inputBar: {
    display: "flex",
    gap: 10,
    padding: "14px 16px",
    borderTop: "1px solid #EFEBE0",
    alignItems: "flex-end",
  },
  textarea: {
    flex: 1,
    resize: "none",
    border: "1px solid #E7E3D8",
    borderRadius: 14,
    padding: "10px 14px",
    fontSize: 14.5,
    fontFamily: "'Inter', sans-serif",
    color: "#20201C",
    maxHeight: 140,
    lineHeight: 1.5,
  },
  sendBtn: {
    border: "none",
    background: "#31304D",
    color: "#F4F2EC",
    fontSize: 14,
    fontWeight: 500,
    padding: "10px 18px",
    borderRadius: 14,
    fontFamily: "'Inter', sans-serif",
  },

  /* Login screen */
  loginCard: {
    width: "100%",
    maxWidth: 360,
    background: "#FFFFFF",
    border: "1px solid #E7E3D8",
    borderRadius: 20,
    boxShadow: "0 20px 50px -20px rgba(30,26,20,0.18)",
    padding: "32px 28px",
    display: "flex",
    flexDirection: "column",
  },
  loginAvatar: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    background: "#31304D",
    color: "#F1ECDF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Fraunces', serif",
    fontWeight: 600,
    fontSize: 18,
    marginBottom: 16,
  },
  loginTitle: { fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: 20, color: "#20201C", marginBottom: 4 },
  loginSubtitle: { fontSize: 13, color: "#8A8578", marginBottom: 22 },
  label: { fontSize: 12.5, color: "#6B6659", marginBottom: 6, fontWeight: 500 },
  textInput: {
    border: "1px solid #E7E3D8",
    borderRadius: 12,
    padding: "10px 12px",
    fontSize: 14,
    fontFamily: "'Inter', sans-serif",
    color: "#20201C",
    marginBottom: 16,
    width: "100%",
  },
  loginError: {
    background: "#FBEAE5",
    color: "#9A3B24",
    fontSize: 12.5,
    padding: "8px 10px",
    borderRadius: 10,
    marginBottom: 14,
  },
  loginBtn: {
    border: "none",
    background: "#31304D",
    color: "#F4F2EC",
    fontSize: 14,
    fontWeight: 500,
    padding: "11px 16px",
    borderRadius: 14,
    fontFamily: "'Inter', sans-serif",
    cursor: "pointer",
  },
  switchModeBtn: {
    border: "none",
    background: "transparent",
    color: "#6B6659",
    fontSize: 12.5,
    marginTop: 12,
    cursor: "pointer",
    fontFamily: "'Inter', sans-serif",
  },
  loginNote: { marginTop: 14, fontSize: 11.5, color: "#B0A891", textAlign: "center" },
};
